import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LoadingSpinner } from '../../../components/LoadingSpinner/LoadingSpinner';
import {
  AssembleItem,
  formTemplateService,
  LibraryForm,
  TemplateSummary,
} from '../../../services/formTemplate.service';
import { StatusBadge, TemplateTypeLabel } from './shared';
import { ServiceProviderPicker } from './ServiceProviderPicker';
import './FormTemplates.scss';

interface Piece {
  key: string;
  item: AssembleItem;
  label: string;
  detail: string;
}

/**
 * Booklet composer — pick library forms and/or already-uploaded SP templates, put them in
 * order (drag, or the arrow buttons) and assemble ONE booklet. Each component's mapping is
 * carried over automatically; only unmapped pages reach the mapping screen.
 */
export const BookletComposer: React.FC = () => {
  const navigate = useNavigate();
  const [library, setLibrary] = useState<LibraryForm[]>([]);
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [spRef, setSpRef] = useState('');
  const [spName, setSpName] = useState('');
  const [spType, setSpType] = useState('');
  const [name, setName] = useState('');
  const [pieces, setPieces] = useState<Piece[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [lib, tpl] = await Promise.all([formTemplateService.library(), formTemplateService.list()]);
        setLibrary(lib);
        setTemplates(tpl.filter((t) => !['FLAT', 'UPLOADED'].includes(t.status)));
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Failed to load components');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const spTemplates = useMemo(
    () => templates.filter((t) => !spRef.trim() || t.spRef.toLowerCase() === spRef.trim().toLowerCase()),
    [templates, spRef],
  );

  const add = (p: Piece) => setPieces((prev) => [...prev, { ...p, key: `${p.key}-${Date.now()}` }]);
  const remove = (i: number) => setPieces((prev) => prev.filter((_, idx) => idx !== i));
  const move = (from: number, to: number) => {
    if (to < 0 || to >= pieces.length) return;
    setPieces((prev) => {
      const next = [...prev];
      const [p] = next.splice(from, 1);
      next.splice(to, 0, p);
      return next;
    });
  };

  const handleAssemble = async () => {
    if (!spRef.trim()) { toast.warn('Choose a service provider'); return; }
    if (pieces.length < 2) { toast.warn('Add at least two documents'); return; }
    try {
      setBusy(true);
      toast.info('Assembling booklet, please wait…');
      const result = await formTemplateService.assemble({
        spRef: spRef.trim(), spName: spName.trim() || undefined, spType: spType || undefined,
        name: name.trim() || undefined, items: pieces.map((p) => p.item),
      });
      toast.success(result.message);
      navigate(`/admin/form-templates/${result.template.id}`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Assembly failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="form-templates">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h1 className="dashboard-title-modern mb-0">Compose a booklet</h1>
          <div className="text-muted small">
            Put the documents in the order the service provider wants them. The system merges them into one PDF and
            reuses every document's existing mapping — only new pages need mapping afterwards.
          </div>
        </div>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/admin/form-templates')}>
          <i className="bi bi-arrow-left me-1" />All templates
        </button>
      </div>

      <div className="card p-3 mb-3">
        <div className="row g-2">
          <div className="col-md-7">
            <ServiceProviderPicker value={spRef} required
              onChange={(o) => { setSpRef(o?.spRef || ''); setSpName(o?.spName || ''); setSpType(o?.spType || ''); }} />
          </div>
          <div className="col-md-5">
            <label className="form-label">Booklet name</label>
            <input className="form-control form-control-sm" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Defaults to the component names" />
          </div>
        </div>
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="row g-3">
          <div className="col-lg-6">
            <div className="card p-3 h-100">
              <h2 className="form-templates__h2">Available documents</h2>
              <div className="form-templates__h3">Library (already mapped)</div>
              <ul className="list-group list-group-flush mb-3">
                {library.map((l) => (
                  <li key={l.code} className="list-group-item d-flex justify-content-between align-items-center px-0">
                    <span><strong>{l.displayName}</strong> <small className="text-muted">{l.fieldCount} fields</small></span>
                    <button className="btn btn-sm btn-outline-primary"
                      onClick={() => add({ key: `L-${l.code}`, item: { sourceType: 'LIBRARY', libraryCode: l.code }, label: l.displayName, detail: 'Library form' })}>
                      Add
                    </button>
                  </li>
                ))}
              </ul>
              <div className="form-templates__h3">Uploaded templates {spRef.trim() ? `for ${spRef.trim()}` : '(all providers)'}</div>
              {spTemplates.length === 0 ? (
                <div className="text-muted small">No uploaded templates{spRef.trim() ? ' for this provider' : ''}.</div>
              ) : (
                <ul className="list-group list-group-flush">
                  {spTemplates.map((t) => (
                    <li key={t.id} className="list-group-item d-flex justify-content-between align-items-center px-0 gap-2">
                      <span>
                        <strong>{t.name}</strong> v{t.version} <StatusBadge status={t.status} /><br />
                        <small className="text-muted">
                          {t.spRef} · {TemplateTypeLabel[t.templateType]} · {t.pageCount} pages · {t.flagged > 0 ? `${t.flagged} unmapped` : 'fully mapped'}
                        </small>
                      </span>
                      <button className="btn btn-sm btn-outline-primary"
                        onClick={() => add({ key: `T-${t.id}`, item: { sourceType: 'TEMPLATE', templateId: t.id }, label: `${t.name} v${t.version}`, detail: `${t.spRef} · ${t.pageCount} pages` })}>
                        Add
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card p-3 h-100">
              <h2 className="form-templates__h2">Booklet order</h2>
              {pieces.length === 0 ? (
                <div className="text-muted small">Add documents from the left, then drag to reorder.</div>
              ) : (
                <ol className="form-templates__sequence">
                  {pieces.map((p, i) => (
                    <li
                      key={p.key}
                      draggable
                      className={dragIndex === i ? 'dragging' : ''}
                      onDragStart={() => setDragIndex(i)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => { if (dragIndex != null) move(dragIndex, i); setDragIndex(null); }}
                      onDragEnd={() => setDragIndex(null)}
                    >
                      <span className="badge rounded-pill bg-dark">{i + 1}</span>
                      <span className="flex-grow-1">
                        <strong>{p.label}</strong><br />
                        <small className="text-muted">{p.detail} · fields prefixed s{i + 1}_</small>
                      </span>
                      <span className="btn-group btn-group-sm">
                        <button className="btn btn-light" onClick={() => move(i, i - 1)} disabled={i === 0} title="Move up"><i className="bi bi-arrow-up" /></button>
                        <button className="btn btn-light" onClick={() => move(i, i + 1)} disabled={i === pieces.length - 1} title="Move down"><i className="bi bi-arrow-down" /></button>
                        <button className="btn btn-light" onClick={() => remove(i)} title="Remove"><i className="bi bi-x-lg" /></button>
                      </span>
                    </li>
                  ))}
                </ol>
              )}
              <div className="mt-3">
                <button className="btn btn-primary btn-sm" onClick={handleAssemble} disabled={busy || pieces.length < 2}>
                  {busy ? 'Assembling…' : `Assemble ${pieces.length} document(s) into one booklet`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
